import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable, toJS } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { AppUI, ExerciseUI } from '@seedlang/state';
import WorksheetGridItem from 'components/worksheet/worksheet_grid_item';
import WorksheetFilter from 'components/worksheet/worksheet_filter';
import { isBlank } from '@seedlang/utils';
import Paginator from 'components/paginator';
import { keys, some, xor } from 'lodash';
import { isPresent } from '@seedlang/utils/src';
import Text from 'components/text';
import Spinner from 'components/spinner';
import { deserialize } from 'serializr';
import { GridItemWorksheet } from '@seedlang/models';
import { Constants } from '@seedlang/constants';
import Link from 'components/link';
import Stack from 'components/layout/stack';
import MobileOnly from 'utils/mobile_only';

const Wrapper = styled.div`
  padding-top: ${props => props.paddingTop};
`;

const Section = styled.div`
  border-radius: 10px;
  margin-bottom: 15px;
  width: 100%;
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: bold;
  padding: 10px 10px 10px 0;
  border-radius: 10px;
  cursor: pointer;
  .fa {
    font-size: 24px;
    margin: 0 10px 0 5px;
    width: 15px;
    text-align: center;
  }
  @media only screen and (max-width : 649px) {
    margin-left: 10px;
  }
`;

const SectionDetails = styled.div`
  font-weight: normal;
  color: gray;
  font-size: 14px;
  margin-right: 5px;
`;

const WorksheetWrapper = styled.div`
  border-radius: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 350px), 1fr));
  gap: 15px;
  min-height: ${props => props.minHeight};

  & > *:only-child {
    max-width: 575px;
    margin: auto;
  }
`;

const AdminLink = styled.span`
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  margin-right: 20px;
  vertical-align: bottom;
`;

@observer
class GridLayout extends React.Component {
  @observable gridIsLoaded = false;
  @observable searchQuery = null;
  @observable closedSections = [];
  @observable gridSectionIsLoading = {};
  @observable loadedTagCategories = AppUI.tagCategoryStore.hasIndexData;
  @observable lastFetchedQueryStrings = null;
  @observable previousSignedInState = AppUI.userIsSignedIn;

  constructor(props) {
    super(props);
    if (!AppUI.layout.isMobile) {
      window.addEventListener('scroll', this.onScroll, true);
    }
    if (this.loadedTagCategories) {
      this.getGrid();
    }
    autorun(() => {
      if (!this.loadedTagCategories && !AppUI.loadingUser) {
        this.loadedTagCategories = true;
        this.loadTags();
      }
    });
    autorun(() => {
      const currentSignedInState = AppUI.userIsSignedIn;
      if (this.previousSignedInState !== currentSignedInState) {
        this.previousSignedInState = currentSignedInState;
        ExerciseUI.gridItemStore.clearIndexData();
        ExerciseUI.worksheetStore.clearIndexData();
        ExerciseUI.recentWorksheetStore.clearIndexData();
        this.loadedTagCategories = false;
      }
    });
  }

  componentDidMount() {
    const closedGridSectionsFromCookie = AppUI.cookieStore.closedGridSections;
    this.closedSections = closedGridSectionsFromCookie ? closedGridSectionsFromCookie.split(',') : [];
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll, true);
  }
  onScroll = () => {
    const page = ExerciseUI.worksheetStore.page;
    const app = document.getElementsByClassName('app')[0];
    if (!this.useGridLayout &&
        ExerciseUI.worksheetStore.requestCounter === 0 &&
        !ExerciseUI.worksheetStore.indexDataFullyLoaded &&
        isPresent(app)) {
      // Calculate remaining scroll space
      const scrollBottom = app.scrollTop + app.clientHeight;
      const scrollThreshold = app.scrollHeight - 250;

      if (scrollBottom >= scrollThreshold) {
        ExerciseUI.worksheetStore.setPage(page + 1);
        ExerciseUI.worksheetStore.getIndex(
          {
            limit: this.worksheetLimit,
            sort: this.sortParameter,
            filters: this.worksheetFilters,
            queryStrings: this.queryStringsWithFilters,
          },
          this.afterOnScroll,
        );
      }
    }
  }
  afterOnScroll = (resp) => {
    ExerciseUI.worksheetStore.setIndexData(resp, {addToIndexData: true});
  }

  @computed get hasVisibleGridItems() {
    if (!ExerciseUI.gridItemStore.hasIndexData) return false;
    return ExerciseUI.gridItemStore.indexData.some(gridItem => 
      gridItem.isVisibleToUser(ExerciseUI.user) && 
      (gridItem.worksheetsCount > 0 || 
       (gridItem.otherPosts && ExerciseUI.worksheetStore.hasIndexData) || 
       (gridItem.mostRecent && ExerciseUI.recentWorksheetStore.hasIndexData))
    );
  }

  @computed get gridLayoutEnabled() {
    return this.props.useGridLayout
      && (!ExerciseUI.site.showWorksheetFilter
        || (!this.hasSelectedFilterTags && AppUI.tagCategoryStore.hasIndexData && isBlank(this.searchQuery)));
  }

  @computed get useGridLayout() {
    return this.gridLayoutEnabled && this.hasVisibleGridItems;
  }

  @computed get worksheetFilters() {
    let filters = {};
    if (this.props.defaultWorksheetFilter) {
      filters = this.props.defaultWorksheetFilter;
    }
    if (this.useGridLayout) {
      filters['grid_items_count'] = 0;
    }
    if (!ExerciseUI.user || !ExerciseUI.user.anyAdmin) {
      filters['published'] = true;
    }
    if (this.searchQuery) {
      filters['name'] = `~${this.searchQuery}~`;
    } else if (isBlank(this.searchQuery) && isPresent(filters['name'])) {
      delete filters['name'];
    } else {
      if (this.props.params?.filter === 'video_clips') {
        filters['podcast_episode_id'] = false;
      } else if (this.props.params?.filter === 'podcasts') {
        filters['podcast_episode_id'] = true;
      }
    }
    return filters;
  }
  onSearchQueryChange = (val) => {
    this.searchQuery = val;
    clearInterval(this.interval);
    if (isBlank(val)) {
      this.getGrid();
    }
    if ( isBlank(val) || val.length > 1) {
      this.interval = setTimeout(this.getGrid, 500);
    }
  }

  @computed get sortParameter() {
    const showFreeWorksheetsFirst = !ExerciseUI.user.isMember || (AppUI.vocabBlockedUntilMigrated || AppUI.exercisesBlockedUntilMigrated);
    return [
      showFreeWorksheetsFirst && '-access_without_membership',
      '-pinned',
      '-content_published_at',
    ].filter(Boolean).join(',');
  }
  getGrid = (params = {}) => {
    this.gridIsLoaded = false;
    if (this.gridLayoutEnabled && (!ExerciseUI.gridItemStore.hasIndexData || params.forceLoad)) {
      AppUI.set('worksheetGridScrollPosition', 0);
      ExerciseUI.gridItemStore.clearFilter();
      ExerciseUI.gridItemStore.getIndex({
        queryStrings: {
          sort: 'position',
          filters: {},
          worksheet_filters: this.worksheetFilters,
          show_all: !this.props.showFilters,
        }}, this.getGridItems);
    } else {
      if (this.recentWorksheetIds && this.useGridLayout) {
        params.excludedWorksheetIds = this.recentWorksheetIds;
      }
      this.getWorksheets(params);
    }
  }

  @computed get worksheetLimit() {
    if (this.useGridLayout) {
      return 6;
    } else if (AppUI.layout.isMobile) {
      return 8;
    } else {
      return 15;
    }
  }
  getWorksheets = (params) => {
    const currentSort = this.sortParameter;
    const currentQueryStrings = JSON.stringify(this.queryStringsWithFilters);
    const filtersHaveChanged = some(keys(this.worksheetFilters)?.map((key => this.worksheetFilters[key] !== ExerciseUI.worksheetStore.getFilterValue(key)))) || some(keys(toJS(ExerciseUI.worksheetStore.filters))?.map((key => this.worksheetFilters[key] !== ExerciseUI.worksheetStore.getFilterValue(key))));
    const sortHasChanged = ExerciseUI.worksheetStore.sort !== currentSort;
    const queryStringsHaveChanged = currentQueryStrings !== this.lastFetchedQueryStrings;
    if (!ExerciseUI.worksheetStore.hasIndexData || filtersHaveChanged || sortHasChanged || queryStringsHaveChanged || (isPresent(params) && params['forceLoad'])) {
      AppUI.set('worksheetGridScrollPosition', 0);
      ExerciseUI.worksheetStore.clearIndexData();
      ExerciseUI.worksheetStore.clearFilter();
      this.lastFetchedQueryStrings = currentQueryStrings;
      ExerciseUI.worksheetStore.getIndex({
        limit: this.worksheetLimit,
        sort: currentSort,
        filters: this.worksheetFilters,
        queryStrings: {
          ...this.queryStringsWithFilters,
          excluded_worksheet_ids: params?.excludedWorksheetIds,
        },
      }, this.afterGetWorksheets);
    } else {
      this.gridIsLoaded = true;
    }
  }
  afterGetWorksheets = (resp) => {
    ExerciseUI.worksheetStore.setIndexData(resp);
    this.gridIsLoaded = true;
  }
  getGridItems = (resp) => {
    ExerciseUI.gridItemStore.setIndexData(resp);
    this.gridIsLoaded = true;
    this.visibleSections = resp.map(item => item.id);

    if (!this.hasVisibleGridItems) {
      this.getWorksheets();
      return;
    }
    
    if (ExerciseUI.gridItemStore.indexData.find(item => item.mostRecent && item.isVisibleToUser(ExerciseUI.user))) {
      ExerciseUI.recentWorksheetStore.getIndex({queryStrings: {show_all: 'true'}}, this.afterGetRecentWorksheets);
    } else {
      this.getWorksheets();
    }
  }
  afterGetRecentWorksheets = (resp) => {
    ExerciseUI.recentWorksheetStore.afterGetIndex(resp);
    this.getWorksheets({excludedWorksheetIds: this.recentWorksheetIds});
  }

  @computed get recentWorksheetIds() {
    return ExerciseUI.worksheetNamespace === 'worksheets' && ExerciseUI.recentWorksheetStore.hasIndexData && ExerciseUI.recentWorksheetStore.indexData.map(item => item.id).join(',');
  }
  onToggleSection = (sectionId) => {
    this.closedSections = xor(this.closedSections, [sectionId]);
    AppUI.cookieStore.set('closedGridSections', this.closedSections.join(','));
  }
  sectionIsOpen = (sectionId) => {
    return !this.closedSections?.includes(sectionId);
  }
  getGridItemWorksheets = (gridItem) => {
    ExerciseUI.gridItemWorksheetStore.getIndex({
      queryStrings: {
        filters: {grid_item_id: gridItem.id},
        limit: Constants.WORKSHEETS_PER_SECTION,
        page: gridItem.gridItemState.currentPage,
        worksheet_filters: this.worksheetFilters,
        show_all: !this.props.showFilters,
      }}, resp => this.afterChangeGridItemPage(gridItem.id, resp));
  }
  onIncrementGridItemPage = (increment, gridItem) => {
    this.gridSectionIsLoading[gridItem.id] = true;
    gridItem.gridItemState.setPage(gridItem.gridItemState.currentPage + increment);
    this.getGridItemWorksheets(gridItem);
  }
  afterChangeGridItemPage = (gridItemId, resp) => {
    this.gridSectionIsLoading[gridItemId] = false;
    if (ExerciseUI.gridItemStore.hasIndexData) {
      ExerciseUI.gridItemStore.indexData.find(item => item.id === gridItemId)['gridItemWorksheets'] = resp.map(item => deserialize(GridItemWorksheet, item));
    }
  }
  loadTags = () => {
    AppUI.tagCategoryStore.getIndex({ids: {groupId: AppUI.user.currentUserGroup?.groupId}}, this.afterLoadTags);
  }
  afterLoadTags = (resp) => {
    AppUI.tagCategoryStore.setIndexData(resp);
    this.getGrid();
  }
  displayTagCategory = (tagCategory) => {
    const routeName = AppUI.routeStore.routeName;
    return (
      (routeName === 'worksheets.index' && tagCategory.displayOnHomeFilters) ||
      (routeName === 'videos.index' && tagCategory.displayOnVideoFilters) ||
      (routeName === 'podcast.index' && tagCategory.displayOnPodcastFilters)
    );
  }

  @computed get showProgressTag() {
    const progressCategory = AppUI.tagCategoryStore.indexData.find(item => item.slug === 'progress' && item.published);
    if (isBlank(progressCategory)) { return false; }
    return this.displayTagCategory(progressCategory) && AppUI.userIsSignedIn;
  }

  @computed get filteredTagCategories() {
    return AppUI.tagCategoryStore.indexData?.filter(item => item.published && item.isWorksheetTag && this.displayTagCategory(item));
  }

  @computed get hasSelectedFilterTags() {
    const worksheetProgressTags = AppUI.cookieStore[`worksheetProgressTags${this.activeView}`];
    const worksheetTagIds = AppUI.cookieStore[`worksheetTagIds${this.activeView}`];
    return (this.showProgressTag && isPresent(worksheetProgressTags)) || (isPresent(worksheetTagIds) && some(this.filteredTagCategories.map(tagCategory => some(tagCategory.tags.map(tag => worksheetTagIds?.split(',').includes(tag.id))))));
  }

  @computed get activeView() {
    if (ExerciseUI.worksheetNamespace === 'worksheets') return 'Home';
    if (ExerciseUI.worksheetNamespace === 'videos') return 'Videos';
    if (ExerciseUI.worksheetNamespace === 'podcast') return 'Podcast';
    return null;
  }

  @computed get filteredWorksheetTagIds() {
    return AppUI.cookieStore[`worksheetTagIds${this.activeView}`];
  }

  @computed get filteredWorksheetProgressTags() {
    if (!this.showProgressTag) return null;
    return AppUI.cookieStore[`worksheetProgressTags${this.activeView}`];
  }

  @computed get queryStringsWithFilters() {
    const queryStrings = {
      show_all: !this.props.showFilters,
    };
    
    if (this.filteredWorksheetTagIds) {
      queryStrings.worksheet_tag_ids = this.filteredWorksheetTagIds;
    }
    if (this.filteredWorksheetProgressTags) {
      queryStrings.worksheet_progress_tags = this.filteredWorksheetProgressTags;
    }
    
    return queryStrings;
  }

  render() {
    return (
      <Wrapper id='page-top' paddingTop={AppUI.layout.isMobile ? '15px' : null}>
        <Stack gap='sm' alignItems='center'>
          {
            this.props.showFilters && ExerciseUI.site.showWorksheetFilter &&
              <WorksheetFilter
                onUpdateFilter={() => this.getGrid({forceLoad: true})}
                onSearchQueryChange={this.onSearchQueryChange}
                searchQuery={this.searchQuery}
                routeName={AppUI.routeStore.routeName}
                showProgressTag={this.showProgressTag}
                filteredTagCategories={this.filteredTagCategories}
                allTagCategories={AppUI.tagCategoryStore.indexData}
                activeView={this.activeView}
                showExerciseView={this.props.showExerciseView}
                onToggleExerciseView={this.props.onToggleExerciseView}
              />
          }
          {
            this.useGridLayout && !this.gridIsLoaded &&
              <Spinner
                background={ExerciseUI.site.accentColor}
              />
          }
          {
            this.useGridLayout && ExerciseUI.gridItemStore.indexData.map(gridItem => {
              if (gridItem.isVisibleToUser(ExerciseUI.user) && (gridItem.worksheetsCount > 0 || (gridItem.otherPosts && ExerciseUI.worksheetStore.hasIndexData) || (gridItem.mostRecent && ExerciseUI.recentWorksheetStore.hasIndexData))) {
                return (
                  <Section
                    key={gridItem.id}
                  >
                    <Name
                      onClick={() => this.onToggleSection(gridItem.id)}
                      id={`${gridItem.id}-page-top`}
                    >
                      <div>
                        <i className={`fa fa-angle-${this.sectionIsOpen(gridItem.id) ? 'down' : 'right'}`} />
                        {gridItem.name}
                      </div>
                      {
                        !gridItem.isAutomatic &&
                          <SectionDetails>
                            {
                              ExerciseUI.user.anyAdmin &&
                                <AdminLink>
                                  <Link
                                    to={{
                                      name: 'creator.grid_items.edit',
                                      params: {gridItemId: gridItem.id},
                                    }}
                                  >
                                    Admin Link
                                  </Link>
                                </AdminLink>
                            }
                            {
                              isPresent(gridItem.filteredWorksheetsCount) &&
                              `${gridItem.filteredWorksheetsCount} Post${gridItem.filteredWorksheetsCount === 0 ? '' : 's'}`
                            }
                          </SectionDetails>
                      }
                    </Name>
                    {
                      this.sectionIsOpen(gridItem.id) &&
                        <WorksheetWrapper
                          id={gridItem.id}
                          minHeight={gridItem.gridItemWorksheets.length > 3 ? 600 : null}
                        >
                          {
                            !gridItem.isAutomatic && !this.gridSectionIsLoading[gridItem.id] && gridItem.gridItemWorksheets.map(gridItemWorksheet => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={gridItemWorksheet.id}
                                  worksheet={gridItemWorksheet.worksheet}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                  activeView={this.activeView}
                                />
                              );
                            })
                          }
                          {
                            gridItem.mostRecent && ExerciseUI.recentWorksheetStore.hasIndexData && ExerciseUI.recentWorksheetStore.indexData.map(item => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={item.id}
                                  worksheet={item}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                  activeView={this.activeView}
                                />
                              );
                            })
                          }
                          {
                            gridItem.otherPosts && ExerciseUI.worksheetStore.indexData.map(item => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={item.id}
                                  worksheet={item}
                                  gridLayout={this.useGridLayout}
                                  activeView={this.activeView}
                                />
                              );
                            })
                          }
                          {
                            this.gridSectionIsLoading[gridItem.id] && [...Array(6)].map((item, index) => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={`placeholder-${index}`}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                  activeView={this.activeView}
                                />
                              );
                            })
                          }
                        </WorksheetWrapper>
                    }
                    {
                      !gridItem.isAutomatic && this.sectionIsOpen(gridItem.id) && gridItem.filteredWorksheetsCount > Constants.WORKSHEETS_PER_SECTION &&
                        <Paginator
                          onClickScrollToTop={AppUI.layout.isMobile}
                          pageTop={`${gridItem.id}-page-top`}
                          hideInput
                          onPagination={increment => this.onIncrementGridItemPage(increment, gridItem)}
                          totalCount={gridItem.filteredWorksheetsCount}
                          currentPage={gridItem.gridItemState.currentPage}
                          limit={Constants.WORKSHEETS_PER_SECTION}
                        />
                    }
                    {
                      gridItem.otherPosts && this.sectionIsOpen(gridItem.id) &&
                        <Paginator
                          onClickScrollToTop={AppUI.layout.isMobile}
                          store={ExerciseUI.worksheetStore}
                          pageTop={`${gridItem.id}-page-top`}
                          queryStrings={{excluded_worksheet_ids: this.recentWorksheetIds, show_all: true}}
                        />
                    }
                  </Section>
                );
              }
              return null;
            })
          }
          {
            !this.useGridLayout && ExerciseUI.worksheetStore.hasIndexData &&
              <Section>
                <WorksheetWrapper>
                  {
                    ExerciseUI.worksheetStore.indexData.map(item => {
                      return (
                        <WorksheetGridItem
                          onUpdateFilter={() => this.getGrid({forceLoad: true})}
                          key={item.id}
                          worksheet={item}
                          gridLayout={this.useGridLayout}
                          activeView={this.activeView}
                        />
                      );
                    })
                  }
                </WorksheetWrapper>
                <MobileOnly>
                  <Paginator
                    onClickScrollToTop
                    pageTop='top'
                    store={ExerciseUI.worksheetStore}
                  />
                </MobileOnly>
              </Section>
          }
          {
            !this.useGridLayout && ExerciseUI.worksheetStore.requestCounter > 0 &&
              <Spinner
                background={ExerciseUI.site.accentColor}
              />
          }
          {
            ExerciseUI.site.showWorksheetFilter && this.gridIsLoaded && !this.useGridLayout && ExerciseUI.worksheetStore.indexData?.length === 0 &&
              <Text center>Sorry, we don't have any post that match these filters!</Text>
          }
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(GridLayout);
